import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import getCart from '../hooks/cart/getCart';
import applyProcessingFeeToOrder from '../hooks/cart/applyProcessingFeeToOrder';
import getPaymentMethods from '../hooks/user/getPaymentMethods';
import { useUser } from './UserContext';
import { 
    KEY_COUPON_LOCATION_ID_ALL, 
    KEY_ORDER_TYPE_DEPOSIT, 
    SESSION_KEY_CART_COUNT_ITEMS, 
    SESSION_KEY_CART_ITEMS, 
    SESSION_KEY_ORDER_UUID,
    SESSION_KEY_PAYMENT_METHOD 
} from '../constants';
import { create, all, BigNumber } from 'mathjs';
import { EnrollmentDetail, PaymentMethodParams } from '../types';
import { calculateDiscountedPrice } from '../utils';

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CouponParams {
    code: string | null;
    value: number | null;
    type: number | null;
    locationId: number | null;
    locationName: string | null;
}

interface CartContextType {
    cartItems: any[];
    setCartItems: React.Dispatch<React.SetStateAction<any[]>>;
    depositTotal: number;
    setDepositTotal: React.Dispatch<React.SetStateAction<number>>;
    depositTotalWithDiscounts: number;
    setDepositTotalWithDiscounts: React.Dispatch<React.SetStateAction<number>>;
    tuitionTotal: number;
    setTuitionTotal: React.Dispatch<React.SetStateAction<number>>;
    checkoutProcessingFee: number;
    setCheckoutProcessingFee: React.Dispatch<React.SetStateAction<number>>;
    checkoutFeeName: string;
    setCheckoutFeeName: React.Dispatch<React.SetStateAction<string>>;
    coupon: {
      locationId: number;
      locationName: string;
      type: number;
      code: string;
      value: number;
    };
    applyCoupon: (couponParams: CouponParams) => void;
    applyProcessingFee: (paymentMethodType: number) => void;
    isApplyingProcessingFee: boolean;
    grandTotal: BigNumber;
    orderType: string | undefined;
    setOrderType: React.Dispatch<React.SetStateAction<string>>;
    fetchCart: () => Promise<boolean | undefined>;
    cartItemsTotal: number;
    incrementCartItemsTotal: () => void;
    decrementCartItemsTotal: () => void;
    setCartItemsTotal: React.Dispatch<React.SetStateAction<number>>;
    paymentMethod: {
        cardType: number; 
        cardNumber: string; 
        cardExpire: string;
        paymentType: number;
    };
    applyPaymentMethod: (paymentMethodParams: PaymentMethodParams) => void;
    resetCartContext: () => void;
    payInFullTotal: BigNumber;
    enrollmentDetails: EnrollmentDetail[];
    setEnrollmentDetails: React.Dispatch<React.SetStateAction<EnrollmentDetail[]>>;
}
interface CartProviderProps {
    children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
    const CartMathConfig = {
        number: 'BigNumber' as const,
        precision: 20
    };
    const math = create(all, CartMathConfig);

    const [cartItems, setCartItems] = useState(() => {
        const storedCartItems = localStorage.getItem(SESSION_KEY_CART_ITEMS);
        return storedCartItems ? JSON.parse(storedCartItems) : {};
    });
    const [depositTotal, setDepositTotal] = useState(0);
    const [depositTotalWithDiscounts, setDepositTotalWithDiscounts] = useState(0);
    const [tuitionTotal, setTuitionTotal] = useState(0);
    const [checkoutFeeName, setCheckoutFeeName] = useState('');
    const [checkoutProcessingFee, setCheckoutProcessingFee] = useState(0);
    const [isApplyingProcessingFee, setIsApplyingProcessingFee] = useState(false);
    const [cartItemsTotal, setCartItemsTotal] = useState(() => {
        const storedCartItemsTotal = localStorage.getItem(SESSION_KEY_CART_COUNT_ITEMS);
        return storedCartItemsTotal ? parseInt(storedCartItemsTotal, 10) : 0;
    });

    const [orderType, setOrderType] = useState<string>('');
    const [coupon, setCoupon] = useState({ code: '', value: 0, type: 0, locationId: 0, locationName: '' });
    const [paymentMethod, setPaymentMethod] = useState(() => {
        const storedPaymentMethod = localStorage.getItem(SESSION_KEY_PAYMENT_METHOD);
        return storedPaymentMethod ? JSON.parse(storedPaymentMethod) : { paymentType: 0, cardType: 0, cardNumber: '', cardExpire: '' };
    });

    const resetCartContext = () => {
        setPaymentMethod({ paymentType: 0, cardType: 0, cardNumber: '', cardExpire: '' });
    };

    const calculateGrandTotal = (
        items: any[]
    ): BigNumber => {
        let total = math.bignumber(0);

        if (Array.isArray(items) && (items.length > 0)) {
            items.forEach((item) => {
                const chargePerPeriod = parseFloat(item.item.chargePerPeriod ? item.item.chargePerPeriod : 0);
                const discountAmountCoupon = parseFloat(item.discountAmountCoupon ? item.discountAmountCoupon : 0);
    
                const discountedPrice = chargePerPeriod - discountAmountCoupon;
                total = math.add(total, math.bignumber(discountedPrice));
            });
        }

        if (orderType !== KEY_ORDER_TYPE_DEPOSIT) {
            total = math.add(total, math.bignumber(checkoutProcessingFee));
        }
        return total as BigNumber;
    };

    const calculatePayInFullTotal = (
        items: any[],
        includeProcessingFee = true,
    ): BigNumber => {
        let total = math.bignumber(0);

        if (Array.isArray(items) && (items.length > 0)) {
            items.forEach((item) => {
                const chargePerPeriod = parseFloat(item.item.chargePerPeriod ? item.item.chargePerPeriod : 0);
                const discountType = item.item.discountType;
                const discountAmount = parseFloat(item.item.discountAmount ? item.item.discountAmount : 0);
                const discountAmountCoupon = parseFloat(item.discountAmountCoupon ? item.discountAmountCoupon : 0);
    
                const discountedPrice = parseFloat(calculateDiscountedPrice(chargePerPeriod, discountType, discountAmount, discountAmountCoupon));
                total = math.add(total, math.bignumber(discountedPrice));
            });
        }

        if (includeProcessingFee) { total = math.add(total, math.bignumber(checkoutProcessingFee)); }
        return total as BigNumber;
    };

    const grandTotal = calculateGrandTotal(cartItems.data);
    const [payInFullTotal, setPayInFullTotal] = useState(() => calculatePayInFullTotal(cartItems));
    const { user } = useUser();

    const applyCoupon = (couponParams: CouponParams) => {
        const { code, value, type, locationId, locationName } = couponParams;
        setCoupon({ code: code ?? '', value: value ?? 0, type: type ?? 0, locationId: locationId ?? 0, locationName: locationName ?? '' });
    };

    useEffect(() => {
        if (Array.isArray(cartItems.data) && (cartItems.data.length > 0) && coupon) {
            const newPayInFullTotal = calculatePayInFullTotal(cartItems.data);
            setPayInFullTotal(newPayInFullTotal);
        }
    }, [coupon, cartItems, checkoutProcessingFee]);

    const applyPaymentMethod = (paymentMethodParams: PaymentMethodParams) => {
        const { paymentType, cardType, cardNumber, cardExpire } = paymentMethodParams;
        const newPaymentMethod = { paymentType: paymentType ?? 0, cardType: cardType ?? 0, cardNumber: cardNumber ?? '', cardExpire: cardExpire ?? '' };
        setPaymentMethod(newPaymentMethod);
        localStorage.setItem(SESSION_KEY_PAYMENT_METHOD, JSON.stringify(newPaymentMethod));
    };

    const incrementCartItemsTotal = () => {
        setCartItemsTotal(cartItemsTotal => cartItemsTotal + 1);
        localStorage.setItem(SESSION_KEY_CART_COUNT_ITEMS, JSON.stringify(cartItemsTotal + 1));
    };

    const decrementCartItemsTotal = () => {
        setCartItemsTotal(cartItemsTotal => cartItemsTotal - 1);
        localStorage.setItem(SESSION_KEY_CART_COUNT_ITEMS, JSON.stringify(cartItemsTotal - 1));
    };

    const calculateTotals = (items: any[]) => {
        if (items && items.length > 0) {
            let newDepositTotal = items.reduce((acc, item) => acc + parseFloat(item.item.depositAmount ? item.item.depositAmount : 0), 0);
            const newTuitionTotal = items.reduce((acc, item) => acc + parseFloat(item.item.chargePerPeriod ? item.item.chargePerPeriod : 0), 0);
            setDepositTotal(newDepositTotal);
            setTuitionTotal(newTuitionTotal);
            setPayInFullTotal(calculatePayInFullTotal(items));

            newDepositTotal -= items.reduce((acc, item) => acc + parseFloat(item.discountAmountDeposit ? item.discountAmountDeposit : 0), 0);
            newDepositTotal = math.max(newDepositTotal, 0);
            setDepositTotalWithDiscounts(newDepositTotal);
        }
    };

    const applyProcessingFee = async (paymentMethodType: number) => {
        // Reset fee and check current totals
        setCheckoutFeeName('');
        setCheckoutProcessingFee(0);
        if (!Array.isArray(cartItems.data) || (cartItems.data.length === 0)) { return; }
        const currentPayInFullTotal = (orderType === KEY_ORDER_TYPE_DEPOSIT) 
            ? math.bignumber(depositTotalWithDiscounts)
            : calculatePayInFullTotal(cartItems.data, false);
        
        const orderId = localStorage.getItem(SESSION_KEY_ORDER_UUID);
        if (!orderId || Number(currentPayInFullTotal) === 0 || paymentMethodType == 0) { return; }

        setIsApplyingProcessingFee(true);
        const processingFeeResponse = await applyProcessingFeeToOrder(orderId, paymentMethodType);
        if (!processingFeeResponse || !processingFeeResponse.data) { setIsApplyingProcessingFee(false); return; }

        const processingFee = processingFeeResponse.data;
        setCheckoutFeeName(processingFee.description);
        setCheckoutProcessingFee(Number((Number(currentPayInFullTotal) * (processingFee.percentage / 100)).toFixed(2)) + processingFee.amount);
        setIsApplyingProcessingFee(false);
    };

    const [enrollmentDetails, setEnrollmentDetails] = useState<EnrollmentDetail[]>([]);

    const fetchCart = async (): Promise<boolean | undefined> => {
        try {
            const response = await getCart();

            if (response) {
                // Re-retrieve primary payment method
                const paymentMethod = await getPaymentMethods();
                if (paymentMethod && paymentMethod.data) {
                    applyPaymentMethod(paymentMethod.data);
                }

                setCheckoutProcessingFee(0);
                setCartItems(response.data);
                calculateTotals(response.data.data);
                const totalItems = response.data?.data?.length || 0;
                setCartItemsTotal(totalItems);
                localStorage.setItem(SESSION_KEY_CART_COUNT_ITEMS, JSON.stringify(totalItems));
                localStorage.setItem(SESSION_KEY_CART_ITEMS, JSON.stringify(response.data.data ?? []));
                applyCoupon({ code: null, value: null, type: null, locationId: null, locationName: null });
                if (response.data.coupons && response.data.coupons.length > 0) {
                    applyCoupon({ code: response.data.coupons[0].id, 
                        value: response.data.coupons[0].coupon.amount, 
                        type: response.data.coupons[0].coupon.type, 
                        locationId: response.data.coupons[0].coupon.locationId, 
                        locationName: response.data.coupons[0].coupon.locationId == KEY_COUPON_LOCATION_ID_ALL ? '' : response.data.coupons[0].coupon.location.name 
                    });
                }
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error fetching cart:', error);
        }
    };

    useEffect(() => {
        if (user) {
            fetchCart();
        }
    }, []);

    const contextValue: CartContextType = {
        cartItems,
        setCartItems,
        depositTotal,
        setDepositTotal,
        depositTotalWithDiscounts,
        setDepositTotalWithDiscounts,
        tuitionTotal,
        setTuitionTotal,
        checkoutProcessingFee,
        setCheckoutProcessingFee,
        checkoutFeeName,
        setCheckoutFeeName,
        coupon,
        applyCoupon,
        applyProcessingFee,
        isApplyingProcessingFee,
        grandTotal,
        orderType,
        setOrderType,
        fetchCart,
        incrementCartItemsTotal,
        decrementCartItemsTotal,
        cartItemsTotal,
        setCartItemsTotal,
        paymentMethod,
        applyPaymentMethod,
        resetCartContext,
        payInFullTotal,
        enrollmentDetails,
        setEnrollmentDetails  
    };

    return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export default CartContext;
